import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { FormShell } from "../../components/forms/FormShell";
import { useCities } from "../../contexts/CitiesProvider";
import {
  getFormClosedMessage,
  isFormClosed,
  isShowSubmissionAfterSubmit,
  resolveInteractionMode,
} from "../../lib/forms/formMeta";
import { isFieldInline } from "../../lib/forms/fieldOptions";
import { getPublicFormUrl } from "../../lib/forms/formUrl";
import { useFormQuery } from "../../hooks/useFormQuery";
import { usePageMeta } from "../../hooks/usePageMeta";
import FormComponent from "./_components/form";
import { ChatbotForm } from "./_components/form/ChatbotForm";
import { QuizForm } from "./_components/form/QuizForm";
import { resolveQuizConfig } from "../../lib/forms/quiz";

function FormPageSkeleton() {
  return (
    <div className="animate-pulse space-y-8 py-4">
      <div className="h-10 w-2/3 max-w-md rounded-lg bg-slate-200" />
      <div className="h-5 w-full max-w-xl rounded bg-slate-100" />
      <div className="h-5 w-4/5 max-w-lg rounded bg-slate-100" />
      <div className="mt-10 space-y-6">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="rounded-xl border border-slate-100 bg-slate-50/80 p-6 space-y-4"
          >
            <div className="h-6 w-40 rounded bg-slate-200" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="h-12 rounded-xl bg-slate-200/80" />
              <div className="h-12 rounded-xl bg-slate-200/80" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FormPage() {
  const { formId } = useParams();
  const { data: formData, isLoading, error } = useFormQuery(formId);
  const { cities } = useCities();
  const cityNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const city of cities) {
      map[String(city.id)] = city.name;
    }
    return map;
  }, [cities]);

  const closed = formData ? isFormClosed(formData) : false;
  const closedMessage = formData
    ? getFormClosedMessage(formData)
    : "Форма закрыта";

  const displaySections = useMemo(() => formData?.sections ?? [], [formData]);
  const interactionMode = resolveInteractionMode(formData);

  const hasInlineFields = useMemo(() => {
    if (!formData) return false;
    const root = formData.fields ?? [];
    const sectionFields = displaySections.flatMap(
      (section) => section.fields ?? [],
    );
    return [...root, ...sectionFields].some((field) => isFieldInline(field));
  }, [formData, displaySections]);

  usePageMeta(
    formData
      ? {
          title: formData.name || "Формы",
          description: formData.description?.trim() || undefined,
          ogUrl: getPublicFormUrl(formData.publicId ?? formId ?? formData.id),
        }
      : undefined,
  );

  return (
    <div className="mx-auto w-full px-4 py-5 sm:px-6 sm:py-8">
      <FormShell appearance={formData?.appearance ?? null} className="mx-auto">
        <article className="form-page-card overflow-visible rounded-2xl border border-slate-200/80 shadow-card-lg">
          <header className="border-b border-slate-100/80 px-5 py-5 sm:px-8 sm:py-6">
            {isLoading ? (
              <FormPageSkeleton />
            ) : error ? (
              <div
                className="form-alert-error rounded-xl border border-red-200 bg-red-50 px-5 py-8 text-center"
                role="alert"
              >
                <p className="font-onest text-red-700">{error}</p>
              </div>
            ) : (
              <>
                <h1 className="form-page__heading font-unbounded text-2xl font-extrabold leading-tight text-ink sm:text-3xl">
                  {formData?.name}
                </h1>
                {formData?.description &&
                !hasInlineFields &&
                interactionMode !== "chatbot" &&
                interactionMode !== "quiz" ? (
                  <p className="form-page__description mt-2 max-w-2xl font-onest text-base leading-relaxed text-ink-muted">
                    {formData.description}
                  </p>
                ) : null}
              </>
            )}
          </header>

          {!isLoading && !error && formData ? (
            <div className="overflow-visible px-5 py-5 sm:px-8 sm:py-6">
              {closed ? (
                <div
                  className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 sm:px-5 sm:py-4"
                  role="status"
                >
                  <p className="font-onest text-sm leading-relaxed text-amber-900 sm:text-base">
                    {closedMessage}
                  </p>
                </div>
              ) : null}
              {interactionMode === "chatbot" ? (
                <ChatbotForm
                  key={formData.id}
                  sections={displaySections}
                  rootFields={formData.fields ?? []}
                  description={formData.description ?? ""}
                  submitDisabled={closed}
                  showSectionLabels={formData.showSectionLabels !== false}
                  showSubmissionAfterSubmit={isShowSubmissionAfterSubmit(
                    formData,
                  )}
                  cityNames={cityNames}
                  formId={formId}
                />
              ) : interactionMode === "quiz" ? (
                <QuizForm
                  key={formData.id}
                  sections={displaySections}
                  rootFields={formData.fields ?? []}
                  description={formData.description ?? ""}
                  submitDisabled={closed}
                  showSectionLabels={formData.showSectionLabels !== false}
                  quiz={resolveQuizConfig(formData)}
                  formId={formId}
                />
              ) : (
                <FormComponent
                  key={formData.id}
                  sections={displaySections}
                  description={formData.description ?? ""}
                  submitDisabled={closed}
                  showSectionLabels={formData.showSectionLabels !== false}
                  sectionDisplayMode={
                    formData.sectionDisplayMode === "step" ? "step" : "all"
                  }
                  showSubmissionAfterSubmit={isShowSubmissionAfterSubmit(
                    formData,
                  )}
                  cityNames={cityNames}
                />
              )}
            </div>
          ) : null}
        </article>
      </FormShell>
    </div>
  );
}
